import { FC, useMemo } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Text, TextLink } from '@frontend/design-system';
import { PromoBanner } from '../../../common/components/promo-banners/promo-banner';
import {
  CALL_INSIGHT_SUMMARY_WRITEBACK_BANNER_BETA_VARIANT_FF,
  CALL_INSIGHT_SUMMARY_WRITEBACK_BANNER_FF,
} from '../../constants';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

interface CallInsightSummaryWritebackBannerProps {
  styles?: Interpolation<Theme>;
  locationId?: string;
  isDemo?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
  onDismiss?: () => void;
}

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-insight-summary-writeback';

export const CallInsightSummaryWritebackBanner: FC<CallInsightSummaryWritebackBannerProps> = ({
  styles,
  locationId,
  isDemo,
  innerRef,
  onDismiss,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });
  const { navigate: navigateToSettings } = useSettingsNavigate();
  const { accessibleLocationData } = useAppScopeStore();
  const isVetVertical = locationId ? accessibleLocationData?.[locationId]?.vertical === Vertical.VET : false;

  const locationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);

  const { ref, handleDismiss, shouldShowBanner, isBetaBanner } = useCallIntelInAppBannerVisibility({
    locationIds,
    isDemo,
    componentId: COMPONENT_ID,
    enabled: true,
    maxVisibilityCount: Infinity,
    maxInteractionCount: 0,
    bannerFF: CALL_INSIGHT_SUMMARY_WRITEBACK_BANNER_FF,
    betaBannerFF: CALL_INSIGHT_SUMMARY_WRITEBACK_BANNER_BETA_VARIANT_FF,
    onDismiss,
  });

  const handleSyncSettingsClick = () => {
    navigateToSettings({
      to: '/organization/sync-management/call-intelligence',
    });
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <PromoBanner
      badge={isBetaBanner ? 'beta' : 'new'}
      containerStyles={styles}
      innerRef={composeRefs(innerRef, ref) as React.Ref<HTMLDivElement>}
      title={
        <Text size='medium'>
          {isVetVertical ? (
            <Trans t={t}>
              Use the <strong>Sync Summary</strong>, button to manually upload the call summary to the patient record
            </Trans>
          ) : (
            <Trans t={t}>
              Use the <strong>Sync Summary</strong>, button to manually upload the call summary to the patient record,
              or <strong>automatic sync</strong> in{' '}
              <TextLink
                onClick={handleSyncSettingsClick}
                trackingId={getInAppAwarenessBannerTrackingId({
                  source: COMPONENT_ID,
                  isBeta: isBetaBanner,
                  action: 'sync-settings-link',
                })}
              >
                Sync Settings
              </TextLink>
            </Trans>
          )}
        </Text>
      }
      dismissTrackingId={getInAppAwarenessBannerTrackingId({
        source: COMPONENT_ID,
        isBeta: isBetaBanner,
        action: 'dismiss',
      })}
      onDismiss={handleDismiss}
      color={isBetaBanner ? 'indigo' : 'seaweed'}
      responsiveBadge={false}
      responsiveActions={false}
    />
  );
};
